import {
  NARROW_PROBLEM_WIDTH,
  PROBLEM_WIDTH,
} from '../problemCanvas/problemCanvasConstants';
import type { ProblemJSDependencies } from '../problemJSDependencies';
import { lineFont } from '../state/font';
import {
  BasicObject,
  GRID_GMM_NAME,
  MATH_IMAGE_GMM_NAME,
  NUMBER_LINE_GMM_NAME,
  getFillerBasicObject,
} from '../uiObjects/basicObject';
import { MathImage } from '../uiObjects/mathImage';
import { STRINGS_AND_VALUES_PARAGRAPH_GMM_NAME } from '../uiObjects/stringsAndValuesParagraph';

import {
  BORDER_WIDTH,
  HelpAnswerRegion,
  getHelpAnswerRegionLabelMetrics,
} from './HelpAnswerRegion';
import {
  GAP_BETWEEN_COLUMNS,
  LABEL_FONT_SIZE,
  MARGIN,
} from './SectionedHelpConstants';

const GAP_BETWEEN_STEPS = 10;
const ZEBRA_GRAY = '#f3f3f3';
const ANSWER_REGION_PADDING = 10;
const MINIMAL_ANSWER_PADDING = BORDER_WIDTH + 1;

export interface TwoColumnHelpSectionDTO {
  label?: string;
  twoColumnHelpSteps: TwoColumnHelpStepDTO[];
  // We assume bottom left cell is answer.
  // If there are 'extra' answer cells above that cell, this indicates how many.
  // For example, if there are 2 answer cells, this number should be 1.
  extraAnswerCells?: number;
  // If true, do not include HelpAnswerRegion for this section
  sectionWithNoAnswer?: boolean;
}

interface TwoColumnHelpStepDTO {
  showWork: any;
  explanation: any;
}

type MiddleSymbolLayoutMode =
  | 'whole-cells'
  | 'centered-middle-symbols'
  | 'statement-extents';

interface StatementExtents {
  maxLeftOffset: number;
  maxRightOffset: number;
}

export class TwoColumnHelpSection extends BasicObject {
  problemJS: ProblemJSDependencies;
  dto: TwoColumnHelpSectionDTO;
  leftColumnCells: BasicObject[] = [];
  rightColumnCells: BasicObject[] = [];
  rows: BasicObject[] = [];
  answerIndex?: number;
  private middleSymbolLayoutMode: MiddleSymbolLayoutMode =
    'centered-middle-symbols';
  private statementExtents?: StatementExtents;

  constructor(
    problemJS: ProblemJSDependencies,
    dto: TwoColumnHelpSectionDTO,
    answerIndex?: number
  ) {
    super();
    this.problemJS = problemJS;
    this.dto = dto;
    this.gmmName = 'twoColumnHelpSection';
    this.answerIndex = answerIndex;

    this.buildLeft();
  }

  // Build static panels for first column.
  // Assumes cells will respect narrow width -- won't work (well) if server provides image that is wider
  buildLeft(): void {
    // Determine which rows are answer rows
    const answerCount = 1 + (this.dto.extraAnswerCells ?? 0);
    const firstAnswerRowIndex =
      this.dto.twoColumnHelpSteps.length - answerCount;
    const skipAnswerRegion = !!this.dto.sectionWithNoAnswer;

    this.leftColumnCells = this.dto.twoColumnHelpSteps.map((step, i) => {
      // If this is an answer row, reduce the max width to account for answer-region padding
      const isAnswerRow = i >= firstAnswerRowIndex && !skipAnswerRegion;
      const leftColumnMaxWidth = PROBLEM_WIDTH - NARROW_PROBLEM_WIDTH;
      let leftCell = this.problemJS.getStaticPanel(
        step.showWork,
        0,
        0,
        undefined,
        undefined,
        leftColumnMaxWidth
      );

      if (!isAnswerRow) {
        return leftCell;
      }

      const answerPadding = this.getAnswerCellPadding(leftCell, isAnswerRow);
      const maxWidth = leftColumnMaxWidth - 2 * answerPadding;

      if (leftCell.viewportW > maxWidth) {
        leftCell = this.problemJS.getStaticPanel(
          step.showWork,
          0,
          0,
          undefined,
          undefined,
          maxWidth
        );
      }

      return leftCell;
    });

    this.middleSymbolLayoutMode = this.computeMiddleSymbolLayoutMode();
    this.statementExtents =
      this.middleSymbolLayoutMode === 'statement-extents'
        ? this.computeStatementExtents()
        : undefined;
  }

  /**
   * Compute left column width using one of three strategies:
   * - Center whole cells when fewer than two rows have usable middle symbols.
   * - Center all middle symbols in the column when that keeps every row at or below NARROW_PROBLEM_WIDTH.
   * - Otherwise, align statement rows to a shared middle-symbol column derived from
   *   the max left and right statement extents.
   *
   * Answer rows follow the active strategy: centered-symbol mode centers the answer-row middle symbol,
   * statement-extents mode centers the whole answer-row statement.
   */
  getLeftColumnWidth(): number {
    let required = this.getWidestLabelWidth();
    const statementCompositeWidth = this.getStatementCompositeWidth();

    for (let i = 0; i < this.leftColumnCells.length; i++) {
      const cell = this.leftColumnCells[i];
      const isAnswerRow = this.isAnswerRow(i);
      const pad = this.getAnswerCellPadding(cell, isAnswerRow);
      let neededColumn = cell.viewportW + 2 * pad;

      if (this.middleSymbolLayoutMode === 'centered-middle-symbols') {
        if (this.hasUsableMiddleSymbol(cell)) {
          neededColumn = this.getCenteredMiddleSymbolWidth(cell, pad);
        }
      } else if (
        !isAnswerRow &&
        statementCompositeWidth !== undefined &&
        this.hasUsableMiddleSymbol(cell)
      ) {
        neededColumn = statementCompositeWidth;
      }

      required = Math.max(required, neededColumn);
    }

    return required;
  }

  private getWidestLabelWidth(): number {
    return Math.max(
      this.getSectionLabelWidth(),
      this.getAnswerRegionLabelWidth()
    );
  }

  private getSectionLabelWidth(): number {
    return this.measureLabelWidth(this.dto.label, 2 * MARGIN);
  }

  private getAnswerRegionLabelWidth(): number {
    if (this.dto.sectionWithNoAnswer) {
      return 0;
    }

    const { ctx } = this.problemJS;
    const widths = [
      getHelpAnswerRegionLabelMetrics(ctx, this.answerIndex).outerWidth,
    ];

    if ((this.dto.extraAnswerCells ?? 0) > 0) {
      widths.push(
        getHelpAnswerRegionLabelMetrics(ctx, undefined, true).outerWidth
      );
    }

    return Math.max(...widths);
  }

  private measureLabelWidth(
    label: string | undefined,
    horizontalPadding: number
  ): number {
    if (!label) {
      return 0;
    }

    const { ctx } = this.problemJS;

    ctx.save();
    ctx.font = `${LABEL_FONT_SIZE}px ${lineFont}`;
    const width = ctx.measureText(label).width + horizontalPadding;

    ctx.restore();

    return width;
  }

  // Layout performance is only guaranteed for panels that respect width limitations,
  // currently: StringsAndValuesParagraph, BulletedListSAVP and MathImage
  buildRight(maxRightColumnWidth: number): void {
    this.rightColumnCells = this.dto.twoColumnHelpSteps.map(step =>
      this.problemJS.getStaticPanel(
        step.explanation,
        0,
        0,
        undefined,
        undefined,
        maxRightColumnWidth
      )
    );
  }

  layoutCells(leftColumnWidth: number, columnGap = GAP_BETWEEN_COLUMNS): void {
    const maxRightWidth = Math.max(
      0,
      ...this.rightColumnCells.map(c => c.viewportW)
    );
    const rowWidth = leftColumnWidth + columnGap + maxRightWidth;

    const answerCount = 1 + (this.dto.extraAnswerCells ?? 0);
    const firstAnswerRowIndex = this.leftColumnCells.length - answerCount;
    const skipAnswerRegion = !!this.dto.sectionWithNoAnswer;

    let nextRowY = 0;

    for (let i = 0; i < this.leftColumnCells.length; i++) {
      const leftCell = this.leftColumnCells[i];
      const rightCell = this.rightColumnCells[i];
      // Only treat as answer row if not skipping answer region
      const isAnswerRow = i >= firstAnswerRowIndex && !skipAnswerRegion;

      if (!this.alignBaselines(leftCell, rightCell)) {
        this.centerVerticallyMaybe(leftCell, rightCell);
      }

      const { row, rowHeight } = this.createRow(
        i,
        leftCell,
        rightCell,
        rowWidth,
        isAnswerRow
      );

      row.viewportY = nextRowY;
      this.add(row);
      this.rows.push(row);

      this.centerLeftCell(leftCell, leftColumnWidth, isAnswerRow);

      // left-align in right column
      rightCell.viewportX = leftColumnWidth + columnGap;

      const cellSlideDown =
        GAP_BETWEEN_STEPS / 2 +
        (isAnswerRow ? ANSWER_REGION_PADDING + LABEL_FONT_SIZE : 0);

      leftCell.viewportY += cellSlideDown;
      rightCell.viewportY += cellSlideDown;

      row.add(leftCell);
      row.add(rightCell);

      if (isAnswerRow) {
        // Add a separate answer region for each answer cell row
        const answerRegion = new HelpAnswerRegion(
          row.viewportY,
          leftColumnWidth,
          rowHeight,
          this.answerIndex,
          answerCount > 1 && i > firstAnswerRowIndex
        );

        this.add(answerRegion);
      }

      nextRowY += row.viewportH;
    }

    this.sizeMeToLowestAndRightmostChildren();
  }

  /**
   * Layout rule:
   * - Whole-cell mode centers every row without considering middle symbols.
   * - Centered-symbol mode centers any available middle symbol within the column, including answer rows.
   * - Statement-extents mode aligns non-answer statements to a shared middle-symbol column built from
   *   max left and right statement extents and centers answer rows as whole cells.
   * - Rows without a usable middle symbol always center the whole cell within the usable area.
   *
   * Usable area is the full column for normal rows. Answer rows use the standard inset for text answers
   * and a smaller inset for edge-to-edge visuals (grids, number lines, and math images).
   */
  private centerLeftCell(
    leftCell: BasicObject,
    leftColumnWidth: number,
    isAnswerRow: boolean
  ): void {
    const pad = this.getAnswerCellPadding(leftCell, isAnswerRow);

    const usableLeft = pad;
    const usableRight = leftColumnWidth - pad;
    const usableCenter = usableLeft + (usableRight - usableLeft) / 2;
    const alignmentX = this.getMiddleSymbolAlignmentX(
      leftColumnWidth,
      isAnswerRow
    );

    const pixelsToMiddleSymbol =
      alignmentX === undefined ? 0 : leftCell.pixelsToMiddleSymbol();

    let desiredX: number;

    if (pixelsToMiddleSymbol === 0 || alignmentX === undefined) {
      desiredX = usableCenter - leftCell.viewportW / 2;
    } else {
      desiredX = alignmentX - pixelsToMiddleSymbol;
    }

    const minX = usableLeft;
    const maxX = usableRight - leftCell.viewportW;

    if (maxX < minX) {
      leftCell.viewportX = minX;
    } else if (desiredX < minX) {
      leftCell.viewportX = minX;
    } else if (desiredX > maxX) {
      leftCell.viewportX = maxX;
    } else {
      leftCell.viewportX = desiredX;
    }
  }

  private getAnswerCellPadding(
    leftCell: BasicObject,
    isAnswerRow: boolean
  ): number {
    if (!isAnswerRow) {
      return 0;
    }

    if (this.useMinimalAnswerPadding(leftCell)) {
      return MINIMAL_ANSWER_PADDING;
    }

    return ANSWER_REGION_PADDING;
  }

  private isAnswerRow(rowIndex: number): boolean {
    if (this.dto.sectionWithNoAnswer) {
      return false;
    }

    const answerCount = 1 + (this.dto.extraAnswerCells ?? 0);
    const firstAnswerRowIndex = this.leftColumnCells.length - answerCount;

    return rowIndex >= firstAnswerRowIndex;
  }

  private getCenteredMiddleSymbolWidth(cell: BasicObject, pad: number): number {
    const pixelsToMiddleSymbol = cell.pixelsToMiddleSymbol();

    if (pixelsToMiddleSymbol === 0) {
      return cell.viewportW + 2 * pad;
    }

    return (
      2 *
        Math.max(pixelsToMiddleSymbol, cell.viewportW - pixelsToMiddleSymbol) +
      2 * pad
    );
  }

  private computeMiddleSymbolLayoutMode(): MiddleSymbolLayoutMode {
    const rowsWithUsableMiddleSymbols = this.leftColumnCells.filter(cell =>
      this.hasUsableMiddleSymbol(cell)
    ).length;

    if (rowsWithUsableMiddleSymbols < 2) {
      return 'whole-cells';
    }

    const centeredWidth = Math.max(
      0,
      ...this.leftColumnCells.map((cell, index) =>
        this.getCenteredMiddleSymbolWidth(
          cell,
          this.getAnswerCellPadding(cell, this.isAnswerRow(index))
        )
      )
    );

    return centeredWidth <= NARROW_PROBLEM_WIDTH
      ? 'centered-middle-symbols'
      : 'statement-extents';
  }

  private getStatementCompositeWidth(): number | undefined {
    if (!this.statementExtents) {
      return undefined;
    }

    return (
      this.statementExtents.maxLeftOffset + this.statementExtents.maxRightOffset
    );
  }

  private getMiddleSymbolAlignmentX(
    leftColumnWidth: number,
    isAnswerRow: boolean
  ): number | undefined {
    if (this.middleSymbolLayoutMode === 'whole-cells') {
      return undefined;
    }

    if (this.middleSymbolLayoutMode === 'centered-middle-symbols') {
      return leftColumnWidth / 2;
    }

    if (isAnswerRow) {
      return undefined;
    }

    const statementCompositeWidth = this.getStatementCompositeWidth();

    if (!this.statementExtents || statementCompositeWidth === undefined) {
      return undefined;
    }

    return (
      (leftColumnWidth - statementCompositeWidth) / 2 +
      this.statementExtents.maxLeftOffset
    );
  }

  private useMinimalAnswerPadding(leftCell: BasicObject): boolean {
    return (
      leftCell.gmmName === GRID_GMM_NAME ||
      leftCell.gmmName === NUMBER_LINE_GMM_NAME ||
      leftCell.gmmName === MATH_IMAGE_GMM_NAME
    );
  }

  private computeStatementExtents(): StatementExtents | undefined {
    let maxLeftOffset = 0;
    let maxRightOffset = 0;
    let foundStatement = false;

    this.leftColumnCells.forEach((cell, index) => {
      if (this.isAnswerRow(index)) {
        return;
      }

      const pixelsToMiddleSymbol = cell.pixelsToMiddleSymbol();

      if (pixelsToMiddleSymbol === 0) {
        return;
      }

      foundStatement = true;
      maxLeftOffset = Math.max(maxLeftOffset, pixelsToMiddleSymbol);
      maxRightOffset = Math.max(
        maxRightOffset,
        cell.viewportW - pixelsToMiddleSymbol
      );
    });

    if (!foundStatement) {
      return undefined;
    }

    return { maxLeftOffset, maxRightOffset };
  }

  private hasUsableMiddleSymbol(cell: BasicObject): boolean {
    return cell.pixelsToMiddleSymbol() !== 0;
  }

  private alignBaselines(
    leftCell: BasicObject,
    rightCell: BasicObject
  ): boolean {
    if (leftCell.isBaselineAware() && rightCell.isBaselineAware()) {
      const leftBaseline = leftCell.getVertToBaseline();
      const rightBaseline = rightCell.getVertToBaseline();
      const maxBaseline = Math.max(leftBaseline, rightBaseline);
      const baselineY = maxBaseline;

      leftCell.viewportY = baselineY - leftBaseline;
      rightCell.viewportY = baselineY - rightBaseline;

      return true;
    }

    return false;
  }

  // If left cell is a MathImage and right cell is a StringsAndValuesParagraph, AND the image has more height
  // than the savp, center the savp vertically to the image
  private centerVerticallyMaybe(
    leftCell: BasicObject,
    rightCell: BasicObject
  ): void {
    if (
      leftCell instanceof MathImage &&
      rightCell.gmmName === STRINGS_AND_VALUES_PARAGRAPH_GMM_NAME
    ) {
      const leftHeight = leftCell.viewportH;
      const rightHeight = rightCell.viewportH;

      if (leftHeight > rightHeight) {
        const extraSpace = leftHeight - rightHeight;

        rightCell.viewportY += extraSpace / 2;
      }
    }
  }

  private createRow(
    rowIndex: number,
    leftCell: BasicObject,
    rightCell: BasicObject,
    rowWidth: number,
    isAnswerRow: boolean
  ): { row: BasicObject; rowHeight: number } {
    // Derive row height from where cell bottoms actually end up
    let rowHeight = Math.max(
      leftCell.viewportY + leftCell.viewportH,
      rightCell.viewportY + rightCell.viewportH
    );

    rowHeight += GAP_BETWEEN_STEPS;

    if (isAnswerRow) {
      rowHeight += LABEL_FONT_SIZE;
      rowHeight += ANSWER_REGION_PADDING * 2;
    }

    const row = getFillerBasicObject(rowWidth, rowHeight);

    row.fill = rowIndex % 2 === 1 ? ZEBRA_GRAY : undefined; // zebra striping

    return { row, rowHeight };
  }

  setRowsToWidth(width: number): void {
    this.rows.forEach(row => {
      row.viewportW = width;
    });
  }
}
